import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = '/api/configurations';

const getAuthHeader = (getState) => {
  const token = getState().auth.token;
  return { headers: { Authorization: `Bearer ${token}` } };
};

export const fetchConfigurations = createAsyncThunk('configurations/fetchAll', async (category, { getState, rejectWithValue }) => {
  try {
      const url = category ? `${API_URL}?category=${category}` : API_URL;
      const response = await axios.get(url, getAuthHeader(getState));
      return response.data;
  
  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
}
);

export const fetchConfigurationsByCategory = createAsyncThunk('configurations/fetchByCategory', async (category, { getState, rejectWithValue }) => {
  try {
      const response = await axios.get(`${API_URL}/category/${category}`, getAuthHeader(getState));
      return response.data;
  
  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
}
);

export const fetchCategories = createAsyncThunk('configurations/fetchCategories', async (_, { getState, rejectWithValue }) => {
  try {
      const response = await axios.get(`${API_URL}/categories`, getAuthHeader(getState));
      return response.data;
  
  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
}
);

export const createConfiguration = createAsyncThunk('configurations/create', async (data, { getState, rejectWithValue }) => {
  try {
      const response = await axios.post(API_URL, data, getAuthHeader(getState));
      return response.data;
  
  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
}
);

export const updateConfiguration = createAsyncThunk('configurations/update', async ({ id, data }, { getState, rejectWithValue }) => {
  try {
      const response = await axios.put(`${API_URL}/${id}`, data, getAuthHeader(getState));
      return response.data;
  
  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
}
);

export const deleteConfiguration = createAsyncThunk('configurations/delete', async (id, { getState, rejectWithValue }) => {
  try {
      const response = await axios.delete(`${API_URL}/${id}`, getAuthHeader(getState));
      return { id, ...response.data };
  
  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
}
);

export const bulkUpdateOrder = createAsyncThunk('configurations/bulkUpdateOrder', async (items, { getState, rejectWithValue }) => {
  try {
      const response = await axios.post(`${API_URL}/bulk-order`, { items }, getAuthHeader(getState));
      return response.data;
  
  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
}
);

export const addVehicleModel = createAsyncThunk('configurations/addVehicleModel', async ({ makeKey, model }, { getState, rejectWithValue }) => {
  try {
      const response = await axios.post(`${API_URL}/vehicle-models/add`, { makeKey, model }, getAuthHeader(getState));
      return response.data;
  
  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
}
);

export const removeVehicleModel = createAsyncThunk('configurations/removeVehicleModel', async ({ makeKey, model }, { getState, rejectWithValue }) => {
  try {
      const response = await axios.post(`${API_URL}/vehicle-models/remove`, { makeKey, model }, getAuthHeader(getState));
      return response.data;
  
  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
}
);

const configurationsSlice = createSlice({
  name: 'configurations',
  initialState: {
    items: {},
    categories: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConfigurations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConfigurations.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
      })
      .addCase(fetchConfigurations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      .addCase(fetchConfigurationsByCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConfigurationsByCategory.fulfilled, (state, action) => {
        state.loading = false;
        const { category, data } = action.payload;
        state.items[category] = data;
      })
      .addCase(fetchConfigurationsByCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload.data;
      })
      .addCase(createConfiguration.fulfilled, (state, action) => {
        const config = action.payload.data;
        if (!state.items[config.category]) {
          state.items[config.category] = [];
        }
        state.items[config.category].push(config);
      })
      .addCase(updateConfiguration.fulfilled, (state, action) => {
        const config = action.payload.data;
        const categoryItems = state.items[config.category];
        if (categoryItems) {
          const index = categoryItems.findIndex(item => item.id === config.id);
          if (index !== -1) {
            categoryItems[index] = config;
          }
        }
      })
      .addCase(deleteConfiguration.fulfilled, (state, action) => {
        const { id } = action.payload;
        Object.keys(state.items).forEach(category => {
          state.items[category] = state.items[category].filter(item => item.id !== id);
        });
      })
      .addCase(addVehicleModel.fulfilled, (state, action) => {
        const config = action.payload.data;
        const categoryItems = state.items[config.category];
        if (categoryItems) {
          const index = categoryItems.findIndex(item => item.id === config.id);
          if (index !== -1) {
            categoryItems[index] = config;
          }
        }
      })
      .addCase(removeVehicleModel.fulfilled, (state, action) => {
        const config = action.payload.data;
        const categoryItems = state.items[config.category];
        if (categoryItems) {
          const index = categoryItems.findIndex(item => item.id === config.id);
          if (index !== -1) {
            categoryItems[index] = config;
          }
        }
      });
  },
});

export const { clearError } = configurationsSlice.actions;
export default configurationsSlice.reducer;
